// Capture task due dates are calendar days, as in the owning task panel.
function day(value) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
}
function date(value) {
    return value ? day(value.toDate()) : 'Unknown';
}
function unique(rows) {
    return [
        ...new Map(rows.map((row)=>[
                row.id,
                row
            ])).values()
    ];
}
// Input must come from authorized owning RPCs. This projection creates no
// persisted alert/status rows: changing an owner row clears its condition.
export function buildPursuitOverview(workspace, areas, now = new Date(), sources = {}) {
    const today = day(now);
    const tasks = unique(workspace.tasks);
    const decisions = unique(workspace.goNoGoDecisions);
    const openTasks = tasks.filter((task)=>task.status === 'open' || task.status === 'in_progress');
    const pending = decisions.filter((decision)=>decision.outcome === 'pending');
    const deferred = unique(sources.records ?? []).filter((record)=>record.isCurrent && record.outcome === 'defer');
    const attention = [];
    for (const task of openTasks){
        if (!task.dueAt) continue;
        const due = date(task.dueAt);
        const remaining = (task.dueAt.toDate().getTime() - now.getTime()) / 86400000;
        if (due > today && remaining > 7) continue;
        attention.push({
            key: `capture_task:${task.id}`,
            title: task.title || 'Untitled task',
            condition: due < today ? 'Overdue task' : 'Task due soon',
            date: due,
            destination: {
                kind: 'task',
                id: task.id
            }
        });
    }
    for (const decision of pending){
        attention.push({
            key: `capture_decision:${decision.id}`,
            title: 'Go/no-go decision',
            condition: 'Pending decision',
            date: date(decision.dueAt),
            destination: {
                kind: 'decision',
                id: decision.id
            }
        });
    }
    for (const area of unique(areas)){
        if (area.health !== 'at_risk') continue;
        attention.push({
            key: `capture_area:${area.id}`,
            title: area.name || 'Unnamed area',
            condition: 'Capture Area at risk',
            date: date(area.targetDate),
            destination: {
                kind: 'area',
                id: area.id
            }
        });
    }
    const activity = [];
    for (const interaction of unique(workspace.interactions)){
        // A masked interaction has no summary. Do not turn its id/date into
        // activity or infer that hidden intelligence is available to the viewer.
        if (!interaction.summary.trim() || !interaction.interactionDate) continue;
        activity.push({
            key: `customer_interaction:${interaction.id}`,
            title: interaction.summary,
            condition: 'Customer interaction',
            date: date(interaction.interactionDate),
            time: interaction.interactionDate.toDate().getTime(),
            destination: {
                kind: 'interaction',
                id: interaction.id
            }
        });
    }
    for (const decision of decisions){
        if (decision.outcome === 'pending' || !decision.decidedAt) continue;
        activity.push({
            key: `capture_decision:${decision.id}`,
            title: `Go/no-go: ${decision.outcome}`,
            condition: 'Decision recorded',
            date: date(decision.decidedAt),
            time: decision.decidedAt.toDate().getTime(),
            destination: {
                kind: 'decision',
                id: decision.id
            }
        });
    }
    for (const record of unique(sources.records ?? [])){
        const scope = record.scopeKind === 'pursuit' ? 'Whole Pursuit' : record.scopeLabel || 'Unnamed workstream';
        const item = {
            key: `go_no_go:${record.id}`,
            title: `Go/no-go: ${record.outcome} · ${scope}`,
            condition: `${record.isCurrent ? 'Current' : 'Earlier'} scoped decision`,
            date: date(record.decidedAt),
            destination: {
                kind: 'scoped-decision',
                id: record.id
            }
        };
        if (record.isCurrent && record.outcome === 'defer') attention.push({
            ...item,
            condition: `Deferred decision · ${scope}`
        });
        if (record.decidedAt) activity.push({
            ...item,
            time: record.decidedAt.toDate().getTime()
        });
    }
    for (const review of unique(sources.reviews ?? [])){
        if (review.status === 'open' && review.reviewDate && review.reviewDate <= day(new Date(now.getTime() + 7 * 86400000))) {
            attention.push({
                key: `capture_review:${review.id}`,
                title: review.title,
                condition: `${review.reviewDate < today ? 'Overdue' : 'Upcoming'} review · ${review.readiness.replace(/_/g, ' ')}`,
                date: review.reviewDate,
                destination: {
                    kind: 'review',
                    id: review.id
                }
            });
        }
        if (review.status === 'closed' && review.closedAt) activity.push({
            key: `capture_review:${review.id}`,
            title: review.title,
            condition: 'Review closed',
            date: date(review.closedAt),
            time: review.closedAt.toDate().getTime(),
            destination: {
                kind: 'review',
                id: review.id
            }
        });
    }
    for (const event of unique(sources.shared ?? [])){
        const task = event.sourceType === 'govcon.capture_tasks' ? tasks.find((task)=>task.id === event.sourceId) : undefined;
        if (event.sourceType === 'govcon.capture_tasks' && (!task || !openTasks.includes(task))) continue;
        if (!task && !event.href) continue;
        const dueIndex = task ? attention.findIndex((item)=>item.destination.kind === 'task' && item.destination.id === task.id) : -1;
        const due = dueIndex >= 0 ? attention.splice(dueIndex, 1)[0] : undefined;
        const item = {
            key: `shared:${event.id}`,
            title: task?.title || event.title,
            condition: due ? `${due.condition} · ${event.body || 'Assigned to you'}` : event.body || 'Shared source changed; review required',
            date: due?.date ?? date(event.createdAt),
            destination: task ? {
                kind: 'task',
                id: task.id
            } : {
                kind: 'source',
                id: event.sourceId,
                href: event.href
            }
        };
        attention.push(item);
        if (event.createdAt && !task) activity.push({
            ...item,
            time: event.createdAt.toDate().getTime()
        });
    }
    const interactions = unique(workspace.interactions).filter((row)=>row.summary.trim()).map((row)=>({
            key: `customer_interaction:${row.id}`,
            title: row.summary,
            condition: 'Customer interaction',
            date: date(row.interactionDate),
            destination: {
                kind: 'interaction',
                id: row.id
            }
        }));
    return {
        nextTaskDate: openTasks.filter((task)=>task.dueAt).map((task)=>date(task.dueAt)).sort()[0] ?? 'Unknown',
        nextReviewDate: (sources.reviews ?? []).filter((review)=>review.status === 'open' && review.reviewDate).map((review)=>review.reviewDate).sort()[0] ?? 'Unknown',
        nextAreaTargetDate: areas.filter((area)=>area.targetDate).map((area)=>date(area.targetDate)).sort()[0] ?? 'Unknown',
        interactions,
        links: [
            ...new Map((sources.links ?? []).map((link)=>[
                    link.key,
                    link
                ])).values()
        ],
        openTasks: openTasks.length,
        overdueTasks: openTasks.filter((task)=>task.dueAt && date(task.dueAt) < today).length,
        pendingDecisions: pending.length + deferred.length,
        legacyPendingDecisions: pending.length,
        deferredDecisions: deferred.length,
        areaSummary: areas.length === 0 ? 'Not started — no Capture Areas. Health unknown.' : `${unique(areas).length} Capture Area${unique(areas).length === 1 ? '' : 's'} — health is set by people, not task completion.`,
        attention,
        activity: activity.sort((a, b)=>b.time - a.time || a.key.localeCompare(b.key)).slice(0, 5)
    };
}
